// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { CCLocalStorageCategory, CCLocalStorageService, IconConstants, MessageBoxComponent } from 'cc-common';

// devextreme imports
import notify from 'devextreme/ui/notify';

// component imports
import { WorkflowInvoiceLineItemsEditorComponent } from '../workflow-invoice-line-items-editor/workflow-invoice-line-items-editor.component';

// service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowSharedService } from '../../_services/workflow-shared.service';

// model imports
import { APBillParameterDto } from '../../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-parameter-dto';
import { APBillReclassEditorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-reclass-editor-result-dto';
import { WorkflowInfo } from '../../_models/workflow-info';
import { WorkFlowParametersDto } from '../../_models/workflow-parameters-dto';

// constant imports
import { Constants } from '../../../_constants/constants';
import { MessageInterval } from '../../../_constants/messages-intervals';
import { StorageKeyConstants } from '../../../_constants/storage-key-constants';

@Component({
    selector: 'app-workflow-reclassify',
    templateUrl: './workflow-reclassify.component.html',
    styleUrls: ['./workflow-reclassify.component.scss']
})
export class WorkflowReclassifyComponent implements OnInit {
    @ViewChild('workflowReclassifyMessageBox', { static: false }) workflowReclassifyMessageBox: MessageBoxComponent;
    @ViewChild('lineItemEditor', { static: false }) lineItemEditor: WorkflowInvoiceLineItemsEditorComponent;

    @Input() apBillDetailParam: APBillParameterDto;

    @Output() splitterPositionChange = new EventEmitter<boolean>();
    @Output() hideSplitter = new EventEmitter<boolean>();
    @Output() pdfLoader = new EventEmitter<boolean>();
    @Output() pdfURL = new EventEmitter<string>();
    @Output() titleChange = new EventEmitter<string>();
    @Output() onClose = new EventEmitter<boolean>();

    reclassifyEditor: APBillReclassEditorResultDto;
    workflowInfo: WorkflowInfo;
    isReclassified = false;

    separateWindow = false;
    pdfViewerToLeft = false;
    settingsMenu: object[] = [];

    pdfWindow: any;
    pdfSource: string = null;
    pdfFileName: string;
    description: string;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE;
    iconHistory: string = IconConstants.HISTORY;
    iconClose: string = IconConstants.CLOSE;
    iconCross: string = IconConstants.CROSS;
    iconCheck: string = IconConstants.CHECK;
    iconDelete: string = IconConstants.DELETE;
    iconRandom: string = IconConstants.RANDOM;

    loadingVisible = false;

    constructor(
        private readonly localStorageService: CCLocalStorageService,
        private readonly helperService: HelperService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly errorService: ServerErrorService
    ) {}

    ngOnInit() {
        this.getSplitterContentPosition();
        this.loadReclassifyEditor();
    }

    onReclassifyClick(): void {
        this.lineItemEditor.lineItemGrid.instance.saveEditData().then(() => {
            this.reclassifyWorkflow();
        });
    }

    onReclassifyClose(): void {
        this.closeReclassify();
    }

    onForceRefresh(): void {
        this.loadReclassifyEditor();
    }

    onDescriptionUpdate(event: string): void {
        this.description = event;
    }

    private configureDefaultValues(): void {
        this.getInvoice();
        this.prepareWorkflowInfo();
    }

    private loadReclassifyEditor(): void {
        this.loadingVisible = true;
        this.workflowSharedService.getReclassifyEditor(this.apBillDetailParam).subscribe(
            (result: APBillReclassEditorResultDto) => {
                this.reclassifyEditor = result;

                const reclassPopupTitle = `Reclass Bill Number ${this.reclassifyEditor.invoiceNumber}`;
                this.titleChange.emit(reclassPopupTitle);

                this.configureDefaultValues();

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private reclassifyWorkflow(): void {
        const modifiedLineItems = this.lineItemEditor.getLineItems();
        if (modifiedLineItems.length > 0) {
            this.reclassifyEditor.apBillReclassInfo.description = this.workflowInfo.description;
            this.reclassifyEditor.apBillReclassInfo.items = modifiedLineItems;

            this.loadingVisible = true;
            this.workflowSharedService.reclassifyBill(this.reclassifyEditor.apBillReclassInfo).subscribe(
                (result) => {
                    notify('Successfully Reclassified.', 'Success', MessageInterval.successMsgInterval);
                    this.isReclassified = true;
                    this.closeReclassify();
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    private prepareWorkflowInfo(): void {
        this.workflowInfo = new WorkflowInfo();
        Object.keys(this.reclassifyEditor).forEach((key) => (this.workflowInfo[key] = this.reclassifyEditor[key]));
    }

    private getInvoice(): void {
        const baseParams: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParams.workflowId = this.reclassifyEditor.apBillReclassInfo.workflowId;
        baseParams.communityId = this.reclassifyEditor.apBillReclassInfo.communityId;

        if (!this.separateWindow) {
            this.pdfLoader.emit(true);
        }
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                if (!!result) {
                    const fileURL: string = this.helperService.getFileUrlFromBlob(result);

                    this.pdfURL.emit(fileURL);
                    if (!this.separateWindow) {
                        this.pdfLoader.emit(false);
                    }
                    if (this.separateWindow) {
                        this.showPDF();
                    }
                }
            },
            (error) => {
                if (error.status === 404) {
                    this.workflowReclassifyMessageBox.showErrorMessage(
                        'Error',
                        'Something went wrong, We are unable to find the invoice. Please try again later.'
                    );
                } else {
                    this.workflowReclassifyMessageBox.showErrorMessage('Error', error.error);
                }
                this.pdfSource = null;
                this.pdfURL.emit(null);
                if (!this.separateWindow) {
                    this.pdfLoader.emit(false);
                }
            }
        );
    }

    private closeReclassify(): void {
        this.onClose.emit(this.isReclassified);
    }

    private getSplitterContentPosition(): void {
        this.separateWindow = false;
        this.pdfViewerToLeft = JSON.parse(
            this.localStorageService.getItem(StorageKeyConstants.wfInvoiceViewerPosition, CCLocalStorageCategory.WFE) ||
                String(Constants.splitterPDFViewerToLeft)
        );
        this.setSettingsMenu();
        this.splitterPositionChange.emit(this.pdfViewerToLeft);
    }

    private setSettingsMenu(): void {
        const settingsMenu = [
            {
                text: 'Show Invoice On Right Pane',
                visible: this.pdfViewerToLeft,
                onClick: () => {
                    this.pdfViewerToLeft = false;
                    this.splitterPositionChange.emit(this.pdfViewerToLeft);
                    this.setSettingsMenu();
                }
            },
            {
                text: 'Show Invoice On Left Pane',
                visible: !this.pdfViewerToLeft,
                onClick: () => {
                    this.pdfViewerToLeft = true;
                    this.splitterPositionChange.emit(this.pdfViewerToLeft);
                    this.setSettingsMenu();
                }
            },
            {
                text: 'Separate Window',
                visible: !this.separateWindow,
                onClick: () => {
                    this.separateWindow = true;
                    this.hideSplitter.emit(this.separateWindow);
                    this.showPDF();
                    this.changeSettingsMenu();
                }
            }
        ];

        this.settingsMenu = settingsMenu;
    }

    private showPDF(): void {
        if (this.pdfWindow && !this.pdfWindow.closed) {
            this.pdfWindow.close();
        }
        this.pdfWindow = window.open(
            this.pdfSource,
            '_blank',
            'location=yes,height=670,width=620,scrollbars=yes,status=yes'
        );
    }

    private changeSettingsMenu(): void {
        const settingsMenu = [
            {
                text: 'Show Side by Side Panel',
                onClick: () => {
                    this.separateWindow = false;
                    this.hideSplitter.emit(this.separateWindow);
                    this.setSettingsMenu();
                }
            }
        ];

        this.settingsMenu = settingsMenu;
    }
}
